/**
 * PushNotificationSettings — "System Alert Preferences" card.
 * Matches the sale-alerts format: On/Off + In App / Email / Push Notification
 * method buttons for each alert type, with a single Save button.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { useAuth } from '../../context/AuthContext';

const SYSTEM_ALERT_TYPES = [
  { key: 'notify_messages', label: 'Messages',                  desc: 'In-app messages from other users' },
  { key: 'notify_nudges',   label: 'System Nudges & Reminders', desc: 'Deliverable reminders and system prompts' },
];

const OWNER_ALERT_TYPES = [
  { key: 'notify_dsu_published', label: 'Daily Sale Update',  desc: 'When a Daily Sale Update report is published' },
  { key: 'notify_sale_result',   label: 'Sale Result',         desc: 'When a horse is sold, RNA, or withdrawn' },
  { key: 'notify_vetting',       label: 'Vetting Procedure',   desc: 'When a new vetting procedure is logged' },
];

// Each pref key maps to { enabled, methods: [] }
// We store this alongside the existing push prefs as "method_<key>": ['portal','email','text']
const DEFAULT_METHODS = { enabled: true, methods: ['portal'] };

const PushNotificationSettings = () => {
  const { user } = useAuth();
  const { isSupported, isSubscribed, loading: pushLoading, subscribe } = usePushNotifications();
  const [rawPrefs, setRawPrefs] = useState(null);
  const isOwner = user?.roles?.includes('owner');
  const allTypes = isOwner ? [...SYSTEM_ALERT_TYPES, ...OWNER_ALERT_TYPES] : SYSTEM_ALERT_TYPES;

  useEffect(() => {
    api.get('/push/preferences').then(r => setRawPrefs(r.data)).catch(() => {});
  }, []);

  // Build per-type pref objects from rawPrefs
  const getTypePref = (key) => {
    if (!rawPrefs) return DEFAULT_METHODS;
    return {
      enabled: rawPrefs[key] !== false,  // default true
      methods: rawPrefs[`methods_${key}`] || ['portal'],
    };
  };

  const updateTypePref = (key, field, value) => {
    const updated = field === 'enabled'
      ? { ...rawPrefs, [key]: value }
      : { ...rawPrefs, [`methods_${key}`]: value };
    setRawPrefs(updated);
    api.put('/push/preferences', updated).catch(() => toast.error('Failed to save'));
  };

  const toggleMethod = (key, method) => {
    const current = rawPrefs?.[`methods_${key}`] || ['portal'];
    const updated = current.includes(method)
      ? current.filter(m => m !== method)
      : [...current, method];
    if (method === 'text' && !current.includes('text') && !isSubscribed) {
      subscribe().then(result => {
        if (!result.success && result.reason === 'permission_denied') {
          toast.error('Notification permission denied. Enable in your browser settings.');
          return;
        }
      });
    }
    const newPrefs = { ...rawPrefs, [`methods_${key}`]: updated };
    setRawPrefs(newPrefs);
    api.put('/push/preferences', newPrefs).catch(() => toast.error('Failed to save'));
  };

  if (!isSupported) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">System Alert Preferences</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Receive alerts on your device even when the app is in the background.
        </p>
      </CardHeader>

      <CardContent>
        {pushLoading || !rawPrefs ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading preferences…
          </div>
        ) : (
          <div className="space-y-3">
            {allTypes.map(({ key, label, desc }) => {
              const pref = getTypePref(key);
              return (
                <div key={key} className="border rounded p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-[10px] text-muted-foreground">{desc}</p>
                    </div>
                    <Button variant={pref.enabled ? 'default' : 'outline'} size="sm"
                      className={`h-7 text-xs ${pref.enabled ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                      onClick={() => updateTypePref(key, 'enabled', !pref.enabled)}>
                      {pref.enabled ? 'On' : 'Off'}
                    </Button>
                  </div>
                  {pref.enabled && (
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {['portal', 'email', 'text'].map(method => (
                        <Button key={method} size="sm"
                          variant={pref.methods.includes(method) ? 'default' : 'outline'}
                          className={`h-6 text-[10px] ${pref.methods.includes(method) ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                          onClick={() => toggleMethod(key, method)}>
                          {method === 'portal' ? 'In App' : method === 'email' ? 'Email' : 'Push Notification'}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PushNotificationSettings;
